/**
 * Comment Line Types (Lignes de commentaires) as defined by DGI Burkina Faso
 * Reference: Specifications Techniques SFE, Section 2.27 and Annexe §7
 *
 * Le SFE doit pouvoir inscrire au moins 8 lignes de commentaire sur la facture.
 * Chaque ligne de commentaire contient une etiquette et un contenu qui sont
 * definis dans le tableau de parametrage "Lignes de commentaires" en annexe.
 */
/**
 * Types de lignes de commentaires definis par la DGI (§7 Annexe)
 *
 * Codes A et B: a definir par l'entreprise
 * Codes C-H: reserves par la DGI pour usage futur
 */
export const COMMENT_LINE_TYPES = {
    /**
     * A - Reference du certificat d'exoneration
     * Applicable: A definir par l'entreprise
     * Etiquette: Ref. exo.
     */
    A: {
        code: "A",
        label: "Réf. exo.",
        description: "Référence du certificat d'exonération",
        isReserved: false,
    },
    /**
     * B - Base juridique
     * Applicable: A definir par l'entreprise
     * Etiquette: Base juridique
     */
    B: {
        code: "B",
        label: "Base juridique",
        description: "Base juridique",
        isReserved: false,
    },
    /**
     * C - Reserve
     * Code reserve par la DGI pour usage futur
     */
    C: {
        code: "C",
        label: null,
        description: null,
        isReserved: true,
    },
    /**
     * D - Reserve
     * Code reserve par la DGI pour usage futur
     */
    D: {
        code: "D",
        label: null,
        description: null,
        isReserved: true,
    },
    /**
     * E - Reserve
     * Code reserve par la DGI pour usage futur
     */
    E: {
        code: "E",
        label: null,
        description: null,
        isReserved: true,
    },
    /**
     * F - Reserve
     * Code reserve par la DGI pour usage futur
     */
    F: {
        code: "F",
        label: null,
        description: null,
        isReserved: true,
    },
    /**
     * G - Reserve
     * Code reserve par la DGI pour usage futur
     */
    G: {
        code: "G",
        label: null,
        description: null,
        isReserved: true,
    },
    /**
     * H - Reserve
     * Code reserve par la DGI pour usage futur
     */
    H: {
        code: "H",
        label: null,
        description: null,
        isReserved: true,
    },
};
/**
 * Nombre maximum de lignes de commentaire par facture (DGI §2.27)
 */
export const MAX_COMMENT_LINES = 8;
/**
 * Obtient un type de ligne de commentaire par son code
 *
 * @param code - Le code de la ligne de commentaire (A-H)
 * @returns La configuration du type de ligne de commentaire
 *
 * @example
 * ```typescript
 * const typeA = getCommentLineType("A");
 * console.log(typeA.label); // "Réf. exo."
 * ```
 */
export function getCommentLineType(code) {
    return COMMENT_LINE_TYPES[code];
}
/**
 * Verifie si un code est un code de ligne de commentaire valide
 *
 * @param code - Le code a valider
 * @returns true si le code est valide (A-H)
 *
 * @example
 * ```typescript
 * isValidCommentLineTypeCode("A") // true
 * isValidCommentLineTypeCode("Z") // false
 * ```
 */
export function isValidCommentLineTypeCode(code) {
    if (typeof code !== "string") {
        return false;
    }
    return code in COMMENT_LINE_TYPES;
}
/**
 * Obtient les types de lignes de commentaire utilisables (non reserves)
 *
 * @returns Liste des types de lignes de commentaire disponibles
 *
 * @example
 * ```typescript
 * const available = getAvailableCommentLineTypes();
 * // Returns types A and B (non-reserved)
 * ```
 */
export function getAvailableCommentLineTypes() {
    return Object.values(COMMENT_LINE_TYPES).filter((type) => !type.isReserved);
}
/**
 * Obtient les types de lignes de commentaire reserves
 *
 * @returns Liste des types de lignes de commentaire reserves par la DGI
 *
 * @example
 * ```typescript
 * const reserved = getReservedCommentLineTypes();
 * // Returns types C, D, E, F, G, H
 * ```
 */
export function getReservedCommentLineTypes() {
    return Object.values(COMMENT_LINE_TYPES).filter((type) => type.isReserved);
}
/**
 * Verifie si un type de ligne de commentaire est reserve
 *
 * @param code - Le code de la ligne de commentaire
 * @returns true si le type est reserve par la DGI
 *
 * @example
 * ```typescript
 * isReservedCommentLineType("A") // false
 * isReservedCommentLineType("C") // true
 * ```
 */
export function isReservedCommentLineType(code) {
    return COMMENT_LINE_TYPES[code].isReserved;
}
//# sourceMappingURL=comment-lines.js.map